import React, { useMemo } from 'react';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import MenuItem from '@material-ui/core/MenuItem';
import { useAppSelector } from 'careand-redux/store';
import CPPUtils from 'services/CPPUtils';
import usePersistedState from 'hooks/usePersistedState';

const PediatricDoseCalculator = ({ patient }) => {
  const patientHealthRecords = useAppSelector((state) => state.healthRecords.byId[patient.id]);

  const initialWeight = useMemo(() => {
    return CPPUtils.getPatientMeasurements(patientHealthRecords).weight || '';
  }, [patientHealthRecords]);

  const [inputs, setInputs] = usePersistedState(
    'PediatricDoseCalculator' + patient.id,
    {
      dose: '',
      doseUnit: 'mcg/kg/dose',
      weight: initialWeight + '',
      weightUnit: 'kg',
      frequency: 'q1 hr',
      concentration: { mg: '', mL: '' },
    },
    true,
  );

  const doseUnitOptions = [
    'mg/kg',
    'mg/kg/day',
    'mg/kg/dose',
    'mcg/kg',
    'mcg/kg/day',
    'mcg/kg/dose',
  ];

  const frequencyOptions = [
    { value: 'qD', label: 'qD' },
    { value: 'BID', label: 'BID' },
    { value: 'TID', label: 'TID' },
    { value: 'QID', label: 'QID' },
    { value: 'q8 hr', label: 'q8 hr' },
    { value: 'q6 hr', label: 'q6 hr' },
    { value: 'q4 hr', label: 'q4 hr' },
    { value: 'q3 hr', label: 'q3 hr' },
    { value: 'q2 hr', label: 'q2 hr' },
    { value: 'q1 hr', label: 'q1 hr' },
  ];

  const frequencyMultiplier = {
    qD: 1,
    BID: 2,
    TID: 3,
    QID: 4,
    'q8 hr': 3,
    'q6 hr': 4,
    'q4 hr': 6,
    'q3 hr': 8,
    'q2 hr': 12,
    'q1 hr': 24,
  };

  const calculateDose = () => {
    const doseNumber = parseFloat(inputs.dose);
    const weightNumber = parseFloat(inputs.weight);

    if (isNaN(doseNumber) || isNaN(weightNumber) || !inputs.frequency) {
      return {
        dailyDose: 0,
        dosePerTime: 0,
        infantDose: 0,
        administrationDose: 0,
        solutionVolume: 0,
      };
    }

    let dailyDose, dosePerTime, infantDose, administrationDose;

    // Calculate daily dose based on the formula:
    // Dose by Weight (mg/day) = Adult Dose in mg/kg or mg/kg/day x Child Weight in kg
    switch (inputs.doseUnit) {
      case 'mg/kg':
      case 'mg/kg/day':
      case 'mcg/kg':
      case 'mcg/kg/day':
        dailyDose = doseNumber * weightNumber;
        break;
      case 'mg/kg/dose':
      case 'mcg/kg/dose':
        dailyDose = doseNumber * weightNumber * frequencyMultiplier[inputs.frequency];
        break;
      default:
        dailyDose = 0;
    }

    // Calculate dose per administration time
    dosePerTime = dailyDose / frequencyMultiplier[inputs.frequency];

    // Convert mcg to mg if necessary
    if (inputs.doseUnit.startsWith('mcg')) {
      dailyDose /= 1000;
      dosePerTime /= 1000;
    }

    let solutionVolume = null;
    if (inputs.concentration.mg && inputs.concentration.mL) {
      const concentrationRatio =
        parseFloat(inputs.concentration.mg) / parseFloat(inputs.concentration.mL);
      solutionVolume = dosePerTime / concentrationRatio;
      infantDose = (dailyDose / concentrationRatio).toFixed(1);
      administrationDose = solutionVolume.toFixed(1);
    }

    return {
      dailyDose,
      dosePerTime,
      solutionVolume,
      infantDose,
      administrationDose,
    };
  };

  const result = calculateDose();

  return (
    <div className="modal">
      <div className="title">Pediatric Dose Calculator</div>
      <div className="padded-box">
        <div className="row">
          <DefaultTextField
            name="dose"
            label="Dose"
            value={inputs.dose}
            onChange={(event) =>
              setInputs({
                ...inputs,
                dose: event.target.value,
              })
            }
            style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
          />
          <DefaultSelectField
            label="Dose Unit"
            name="doseUnit"
            value={inputs.doseUnit}
            onChange={(event) =>
              setInputs({
                ...inputs,
                doseUnit: event.target.value,
              })
            }
            inputs={doseUnitOptions}
            style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
          />
        </div>

        <div className="row">
          <DefaultTextField
            name="weight"
            label="Infant/Child Weight"
            value={inputs.weight}
            onChange={(event) =>
              setInputs({
                ...inputs,
                weight: event.target.value,
              })
            }
            style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
          />
          <DefaultSelectField
            label="Weight Unit"
            name="weightUnit"
            value={inputs.weightUnit}
            disabled={true}
            onChange={(event) =>
              setInputs({
                ...inputs,
                weightUnit: event.target.value,
              })
            }
            inputs={['kg']}
            style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
          />
        </div>

        <div className="row">
          <DefaultSelectField
            label="Dose Frequency"
            name="frequency"
            value={inputs.frequency}
            onChange={(event) =>
              setInputs({
                ...inputs,
                frequency: event.target.value,
              })
            }
            inputs={frequencyOptions.map((opt) => opt.value)}
            inputElements={frequencyOptions.map((option) => (
              <MenuItem value={option.value} key={option.value}>
                {option.label}
              </MenuItem>
            ))}
            style={{ width: 'calc(100% - 15px)', marginRight: '15px' }}
          />
        </div>

        <div className="row">
          <div
            style={{
              width: 'calc(100% - 15px)',
              marginRight: '15px',
              marginTop: '15px',
              position: 'relative',
            }}
          >
            <label
              className="MuiFormLabel-root MuiInputLabel-root MuiInputLabel-formControl MuiInputLabel-animated MuiInputLabel-shrink MuiFormLabel-filled"
              style={{ opacity: '0.55' }}
            >
              Drug Concentration (Optional)
            </label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <DefaultTextField
                name="concentration_mg"
                placeholder="mg"
                value={inputs.concentration.mg}
                onChange={(event) =>
                  setInputs({
                    ...inputs,
                    concentration: {
                      ...inputs.concentration,
                      mg: event.target.value,
                    },
                  })
                }
                style={{ flex: 1 }}
              />
              <span>/</span>
              <DefaultTextField
                name="concentration_ml"
                placeholder="mL"
                value={inputs.concentration.mL}
                onChange={(event) =>
                  setInputs({
                    ...inputs,
                    concentration: {
                      ...inputs.concentration,
                      mL: event.target.value,
                    },
                  })
                }
                style={{ flex: 1 }}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <DefaultTextField
            name="daily_dose"
            label="Daily Dose"
            value={`${result.dailyDose.toFixed(2)} mg/day`}
            disabled={true}
            style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
          />
          <DefaultTextField
            name="dose_per_time"
            label="Dose per Administration"
            value={`${result.dosePerTime.toFixed(2)} mg`}
            disabled={true}
            style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
          />
        </div>

        {result.infantDose > 0 && result.administrationDose > 0 && (
          <div className="row">
            <DefaultTextField
              name="infant_dose"
              label="Infant/Child Dose"
              value={`${result.infantDose} mL/day`}
              disabled={true}
              style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
            />
            <DefaultTextField
              name="administration_dose"
              label={`For administration ${inputs.frequency}`}
              value={`${result.administrationDose} mL/dose`}
              disabled={true}
              style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PediatricDoseCalculator;
